
import type { Content } from 'vanilla-jsoneditor'
import type Vue from 'vue'
import type { Route } from 'vue-router'
import type { GuardResolveFunction } from '../../../models'
import { computed, defineComponent, getCurrentInstance, ref } from 'vue'
import ExplainKpiMetrics from '../../../components/explain/explain-kpi-metrics.component.vue'
import CompareMode from '../../../components/explain/explain-products-analysis/compare-mode.component.vue'
import ExplainProductAnalysisHeader from '../../../components/explain/explain-products-analysis/header.component.vue'
import NavigateToExplainSearchButton from '../../../components/explain/explain-products-analysis/navigate-to-explain-search-button.component.vue'
import ProductDetails from '../../../components/explain/explain-products-analysis/product-details.component.vue'
import ProductScoreOverview from '../../../components/explain/explain-products-analysis/product-score-overview.component.vue'
import ExplainSectionLayout from '../../../components/explain/explain-section-layout.component.vue'
import JsonEditor from '../../../components/json-editor.component.vue'
import EpbDividedLayout from '../../../components/shared/epb-divided-layout/epb-divided-layout.component.vue'
import FieldValueSelect from '../../../components/shared/field-value-select.component.vue'
import XExtraParams from '../../../components/x/x-extra-params.component.vue'
import { useInstance } from '../../../composables'
import { useExplainBrowse } from '../../../composables/explain/use-explain-browse'
import { useExplainProductAnalysisAction } from '../../../composables/explain/use-explain-product-analysis-action'
import { useGetProductDetails } from '../../../composables/explain/use-get-product-details'
import { useContextQuery } from '../../../composables/use-context-query/index'
import { useExplainFilters } from '../../../composables/use-explain-filters'
import { useExplainSectionsConfig } from '../../../composables/use-explain-sections-config'
import { useResultInfo } from '../../../composables/use-result-info'
import { ExplainSectionEnum } from '../../../enums/explain-section.enum'
import { isExplainRoute } from '../../../router/routes/explain.routes'

export default defineComponent({
  name: 'ExplainProductAnalysis',
  components: {
    JsonEditor,
    ExplainSectionLayout,
    ExplainProductAnalysisHeader,
    ProductDetails,
    NavigateToExplainSearchButton,
    ExplainKpiMetrics,
    EpbDividedLayout,
    ProductScoreOverview,
    CompareMode,
    FieldValueSelect,
    XExtraParams,
  },
  async beforeRouteLeave(to: Route, from: Route, next: GuardResolveFunction): Promise<void> {
    if (this.query && !isExplainRoute(to)) {
      await this.clearQuery()
    }

    next()
  },
  props: {
    extraParamsComponent: {
      type: Object,
      required: false,
    },
  },
  setup() {
    const {
      proxy: { $route, $epb },
    } = getCurrentInstance() as { proxy: Vue }

    const { tagExplainProductAnalysis, navigateToExplainSearch } = useExplainProductAnalysisAction()

    const { query, clearQuery } = useContextQuery()
    const { getResult } = useGetProductDetails({
      productId: $route.params.productId,
    })
    const { currentSectionConfig } = useExplainSectionsConfig(ExplainSectionEnum.PRODUCT_ANALYSIS)
    const { filters } = useExplainFilters({
      areAllFiltersMandatory: currentSectionConfig.value?.areAllFiltersMandatory ?? false,
    })
    const explainBrowse = useExplainBrowse()

    const { data: resultInfoData, error: resultInfoError, fetch: getResultInfo } = useResultInfo()

    const { instanceConfig } = useInstance()

    if (instanceConfig.value.allowShowProductInfo) {
      getResultInfo({
        id: $route.params.productId,
        lang: filters.value.lang.selectedValues?.[0],
      })
    }

    const infoModeContent = computed<Content>(() => {
      let json = {}
      if (resultInfoData.value) {
        json = Object.keys(resultInfoData.value)
          .sort()
          .reduce((res, key) => ({ ...res, [key]: resultInfoData.value[key] }), {})
      } else if (resultInfoError.value) {
        json = resultInfoError.value
      }
      return { json }
    })

    const result = ref(getResult())

    const isCompareModeEnabled = ref(false)

    const isInfoModeEnabled = ref(false)

    if (!result.value) {
      navigateToExplainSearch()
    }

    tagExplainProductAnalysis()

    function handleCloseInfoMode(): void {
      isInfoModeEnabled.value = false
    }

    function handleCompareMode(event): void {
      handleCloseInfoMode()
      isCompareModeEnabled.value = event
    }

    function handleCloseCompareMode(): void {
      isCompareModeEnabled.value = false
    }

    async function handleInfoMode(event: boolean): Promise<void> {
      handleCloseCompareMode()
      isInfoModeEnabled.value = event
    }

    return {
      baseClass: 'epb-explain-product-analysis',
      messages: $epb.messages.explain,
      result,
      position: result.value?.debug?.position,
      query,
      productId: $route.params.productId,
      clearQuery,
      handleCompareMode,
      handleCloseCompareMode,
      handleInfoMode,
      handleCloseInfoMode,
      isCompareModeEnabled,
      isInfoModeEnabled,
      infoModeContent,
      ...explainBrowse,
      filters,
    }
  },
})
